import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { ZmanimData } from '../types';
import { computeZmanimForDate } from '../utils/hebcalZmanim';
import { normalizeZmanim } from '../utils/zmanimRules';
import { getTodayString } from '../utils/dateShortcuts';

export type ZmanimSource = 'myzmanim' | 'hebcal';

/**
 * Zmanim for the Calendar tab.
 * - Today: try /zmanimCache (MyZmanim), fall back to Hebcal.
 * - Any other date: Hebcal only — never read the cache (docs only hold today/tomorrow).
 */
export function useCalendarZmanim(date: string) {
  const [zmanim, setZmanim] = useState<ZmanimData | null>(null);
  const [source, setSource] = useState<ZmanimSource | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      const today = getTodayString();

      try {
        if (date === today) {
          const docSnap = await getDoc(doc(db, 'zmanimCache', date));
          if (cancelled) return;
          if (docSnap.exists()) {
            setZmanim(normalizeZmanim(docSnap.data() as ZmanimData));
            setSource('myzmanim');
          } else {
            setZmanim(computeZmanimForDate(date));
            setSource('hebcal');
          }
        } else {
          setZmanim(computeZmanimForDate(date));
          setSource('hebcal');
        }
      } catch (err: unknown) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setZmanim(null);
        setSource(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [date]);

  return { zmanim, source, loading, error };
}
